import { createAuthenticatedClient } from "../utils/apiClient";
import { getStoredToken } from "../utils/installAuthToken";

// PlacingAPI: the open-market placing flow, from starting a placement to
// nominating the lead(s). Every call goes through the authenticated client,
// which unwraps the envelope and rejects with error.code / error.message.
export const PLACING_API = "https://placing.poc.mrc.ai/api";

const client = () => {
  const token = getStoredToken();
  if (!token) throw new Error("No authentication token available");
  return createAuthenticatedClient(token);
};

const get = async (path) => (await client().get(`${PLACING_API}${path}`)).data;
const post = async (path, body = {}) => (await client().post(`${PLACING_API}${path}`, body)).data;
const put = async (path, body = {}) => (await client().put(`${PLACING_API}${path}`, body)).data;

/** Items from a paged result ({ items, pagination }); a plain array as-is. */
export const pageItems = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

// --- Placements ---

/** Creates the placement and its draft submission: { placement, submission }. */
export const startPlacement = (body) => post("/placements/start", body);

export const getPlacementForDocument = (documentId) =>
  get(`/placements/document/${documentId}`);

export const getPlacement = (placementId) => get(`/placements/${placementId}`);

/** Paged placements owned by the user's organisations, with the API's filters. */
export const listPlacements = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries({ page: 1, pageSize: 25, ...params }).filter(([, v]) => v !== "" && v != null)
  );
  return get(`/placements?${query}`);
};

/**
 * Every record of a placing resource the user can see (up to the API's page
 * limit of 1000): placements' submissions, quoterequests, quotes,
 * linerequests, offers, signedlineadvices.
 */
export const listAll = async (resource) => pageItems(await get(`/${resource}?page=1&pageSize=1000`));

/** Every placement header (no paging), for counts and name lookups. */
export const listPlacementHeaders = () => get("/placements/headers");

// --- Submissions ---

export const listSubmissions = ({ page = 1, pageSize = 50 } = {}) =>
  get(`/submissions?page=${page}&pageSize=${pageSize}`);

/** The submission for a document (how a carrier finds it from the document). */
export const getSubmissionForDocument = (documentId) => get(`/submissions/document/${documentId}`);

export const getSubmissionsForPlacement = (placementId) =>
  get(`/submissions/placement/${placementId}`);

/** Opening it as a carrier records that they've viewed it. */
export const getSubmission = (submissionId) => get(`/submissions/${submissionId}`);

/** Replaces the submission (only while it's a draft: 409 SUBMISSION_LOCKED once shared). */
export const updateSubmission = (submission) => put(`/submissions/${submission._id}`, submission);

/** The full list the broker wants; parties already sent it always stay. */
export const setPotentialLeads = (submissionId, partyIds) =>
  put(`/submissions/${submissionId}/potential-leads`, { partyIds });

export const shareSubmission = (submissionId, responseRequiredByDate) =>
  post(
    `/submissions/${submissionId}/share`,
    responseRequiredByDate ? { responseRequiredByDate } : {}
  );

/** Carrier response at build: decline to participate (reason required). */
export const declineSubmission = (submissionId, { reason, reasonCode, partyId }) =>
  post(`/submissions/${submissionId}/decline`, {
    ...(reason && { reason }),
    ...(reasonCode && { reasonCode }),
    ...(partyId && { partyId }),
  });

/** Carrier response at build: declare interest, with optional notes. */
export const declareInterest = (submissionId, { notes, partyId }) =>
  post(`/submissions/${submissionId}/declare-interest`, { ...(notes && { notes }), ...(partyId && { partyId }) });

export const createQuoteRequests = (submissionId, body) =>
  post(`/submissions/${submissionId}/quoterequests`, body);

export const compareQuotes = (submissionId) => get(`/submissions/${submissionId}/quotes`);

/** ntuReasonCode (B22) and ntuNotes are needed when other quotes or open quote requests will be NTU'd. */
export const nominateLeads = (submissionId, quoteIds, { ntuReasonCode, ntuNotes } = {}) =>
  post(`/submissions/${submissionId}/nominate`, {
    quoteIds,
    ...(ntuReasonCode && { ntuReasonCode }),
    ...(ntuNotes && { ntuNotes }),
  });

// --- Quote requests and quotes ---

export const listQuoteRequests = ({ page = 1, pageSize = 50 } = {}) =>
  get(`/quoterequests?page=${page}&pageSize=${pageSize}`);

export const getQuoteRequest = (quoteRequestId) => get(`/quoterequests/${quoteRequestId}`);

/** A placement's quote requests (several can go to one carrier). */
export const getQuoteRequestsForPlacement = (placementId) =>
  get(`/quoterequests/placement/${placementId}`).then(pageItems);

/** Stamps and sends a quote. */
export const sendQuote = (quoteRequestId, body) =>
  post(`/quoterequests/${quoteRequestId}/quotes`, body);

/** Decline to quote: reasonCode (B22) required; reason text required when the code is "other". */
export const declineQuoteRequest = (quoteRequestId, { reason, reasonCode }) =>
  post(`/quoterequests/${quoteRequestId}/decline`, { ...(reason && { reason }), reasonCode });

export const getQuote = (quoteId) => get(`/quotes/${quoteId}`);

// --- Order: line requests (lead and followers) and offers (written lines) ---

/** Requests lines: role "lead" (nominated leads) or "follower"; one line request per party. */
export const requestLines = (placementId, body) => post(`/placements/${placementId}/linerequests`, body);

/** The placement's lines: line requests by role and carrier, each with its offers. */
export const getPlacementLines = (placementId) => get(`/placements/${placementId}/lines`);

export const getLineRequest = (lineRequestId) => get(`/linerequests/${lineRequestId}`);

/** Stamps and sends a line (offer); conditions make it a conditional line. */
export const sendOffer = (lineRequestId, body) => post(`/linerequests/${lineRequestId}/offers`, body);

/** Decline line: reasonCode (B22) required; reason text required when the code is "other". */
export const declineLineRequest = (lineRequestId, { reason, reasonCode }) =>
  post(`/linerequests/${lineRequestId}/decline`, { ...(reason && { reason }), reasonCode });

export const getOffer = (offerId) => get(`/offers/${offerId}`);

// --- Close the risk and sign down (once per placement) ---

/**
 * What signing down would do, changing nothing. Without offerIds the API
 * picks each carrier's latest unconditional, unexpired offer.
 */
export const previewSignDown = (placementId, offerIds, { acceptConditions } = {}) =>
  post(`/placements/${placementId}/sign-down/preview`, {
    ...(offerIds && { offerIds }),
    ...(acceptConditions && { acceptConditions: true }),
  });

/** Closes the risk to further lines and signs down: sends the signed line advices, NTUs the rest. */
/**
 * acceptConditions accepts the conditions on every conditional line signed;
 * send the same offerIds and acceptConditions the preview used.
 */
export const signDown = (placementId, { offerIds, acceptConditions, notes, ntuReasonCode, ntuNotes }) =>
  post(`/placements/${placementId}/sign-down`, {
    offerIds,
    acceptConditions: !!acceptConditions,
    ...(notes && { notes }),
    ...(ntuReasonCode && { ntuReasonCode }),
    ...(ntuNotes && { ntuNotes }),
  });

export const getSignedLineAdvicesForPlacement = (placementId) =>
  get(`/signedlineadvices/placement/${placementId}`).then(pageItems);

export const getSignedLineAdvice = (signedLineAdviceId) => get(`/signedlineadvices/${signedLineAdviceId}`);

/** IDs from a placement's or quote request's link lists ({ value } wrappers or plain IDs). */
export const linkedIds = (links) =>
  (links || []).map((l) => (typeof l === "string" ? l : l?.value || l?._id)).filter(Boolean);
